import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import Button from "../../Common/Button";
import DropDown from "../../Common/DropDown";
import Input from "../../Common/V2/Widgets/Input/index";
import styles from "./beneficiary.module.css";
import { Spinner } from "../../Common";
import singleBackArrow from "../../public/images/singleBackArrow.svg";
import {
  lienCountryList,
  lienBeneCountryList,
  AddNewBeneficiary,
  LienBeneficiary,
} from "../../api";
import {
  ENTITY_OPTIONS,
  SKIP_DYNAMIC_RENDER,
  ADDRESS_FIELD_NAMES,
  INITIAL_FORM,
  STEP_MAIN,
  STEP_ADDRESS,
  getFirstValidRoutingType,
  formatDisplayName,
  capitaliseFirst,
  trimFormData,
  buildMethodsMap,
  Address_Fields,
  ADDRESS__MAP,
} from "../../../src/utils/utils";
import { validateMainStep, validateAddressStep } from "./BeneficiaryValidation";

const POLL_DELAYS = [3000, 6000, 10000];

const pollForStatus = async (userId, newBeneficiaryId) => {
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  for (let i = 0; i < POLL_DELAYS.length; i++) {
    await wait(POLL_DELAYS[i]);
    try {
      const statusRes = await LienBeneficiary(userId, 1, 50, "");
      const list = statusRes?.data?.data?.beneficiaries || [];
      const matched = list.find((b) => b.beneficiaryId === newBeneficiaryId);
      if (matched?.status === "APPROVED" || matched?.status === "REJECTED") {
        return matched;
      }
    } catch {}
  }
  return null;
};

function LienAddBeneficiary({ userId, onSuccess, onCancel }) {
  const [step, setStep] = useState(STEP_MAIN);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [pollingMessage, setPollingMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [countryList, setCountryList] = useState([]);
  const [countryLoading, setCountryLoading] = useState(false);
  const [methods, setMethods] = useState({});
  const [methodsLoading, setMethodsLoading] = useState(false);
  const [rawFields, setRawFields] = useState([]);
  const [accountFields, setAccountFields] = useState([]);
  const [addressFields, setAddressFields] = useState([]);

  useEffect(() => {
    const fetchCountries = async () => {
      setCountryLoading(true);
      try {
        const res = await lienCountryList({}, userId);
        if (res?.data?.data) setCountryList(res.data.data);
      } catch (err) {
        console.error("Error fetching countries:", err);
      } finally {
        setCountryLoading(false);
      }
    };
    fetchCountries();
  }, [userId]);

  useEffect(() => {
    if (!formData.country_code || !formData.account_currency) return;

    const fetchMethods = async () => {
      setMethodsLoading(true);
      setMethods({});
      setRawFields([]);
      setAccountFields([]);
      setAddressFields([]);
      setStep(STEP_MAIN);
      setFormData((prev) => ({
        ...prev,
        payment_method: "",
        transfer_method: "",
      }));

      try {
        const res = await lienBeneCountryList(
          {},
          formData.account_currency,
          formData.country_code,
          userId,
        );

        const methodsFromApi = res?.data?.data?.[0]?.methods || [];
        const built = buildMethodsMap(methodsFromApi);
        setMethods(built);

        if (Object.keys(built).length === 1) {
          const onlyMethod = Object.keys(built)[0];
          setFormData((prev) => ({
            ...prev,
            payment_method: onlyMethod,
            transfer_method: onlyMethod,
          }));
        }
      } catch (err) {
        console.error("Error fetching methods:", err);
      } finally {
        setMethodsLoading(false);
      }
    };

    fetchMethods();
  }, [formData.country_code]);

  useEffect(() => {
    if (
      !formData.payment_method ||
      !formData.country_code ||
      !formData.account_currency
    )
      return;

    const fetchFieldsByMethod = async () => {
      setMethodsLoading(true);
      setRawFields([]);
      try {
        const res = await lienBeneCountryList(
          {},
          formData.account_currency,
          formData.country_code,
          userId,
          formData.payment_method,
        );
        const methodsFromApi = res?.data?.data?.[0]?.methods || [];
        const built = buildMethodsMap(methodsFromApi);
        const fields = built[formData.payment_method]?.fields || [];
        setRawFields(fields);
      } catch (err) {
        console.error("Error fetching fields for method:", err);
      } finally {
        setMethodsLoading(false);
      }
    };

    fetchFieldsByMethod();
  }, [formData.payment_method]);

  useEffect(() => {
    if (!rawFields.length) {
      setAccountFields([]);
      setAddressFields([]);
      return;
    }

    const formatted = rawFields.map((f) => ({
      ...f,
      displayName: formatDisplayName(f.displayName || f.fieldName),
    }));

    const accFields = formatted.filter(
      (f) =>
        f.required === true &&
        !SKIP_DYNAMIC_RENDER.has(f.fieldName) &&
        !ADDRESS_FIELD_NAMES.has(f.fieldName),
    );

    const addrFields = Address_Fields.map((name) => {
      const apiField = formatted.find((f) => f.fieldName === name);

      return {
        fieldName: name,
        displayName: ADDRESS__MAP[name] || name,
        required: apiField?.required || false,
        validation: apiField?.validation || "",
        dataType: apiField?.dataType || "string",
      };
    });

    setAccountFields(accFields);
    setAddressFields(addrFields);

    const initValues = formatted.reduce((acc, f) => {
      if (SKIP_DYNAMIC_RENDER.has(f.fieldName)) return acc;
      if (formData[f.fieldName] !== undefined && formData[f.fieldName] !== "")
        return acc;

      if (f.fieldName === "account_routing_type1") {
        acc[f.fieldName] = getFirstValidRoutingType(
          formatted,
          "account_routing_type1",
        );
      } else if (f.fieldName === "account_routing_type2") {
        acc[f.fieldName] = getFirstValidRoutingType(
          formatted,
          "account_routing_type2",
        );
      } else {
        acc[f.fieldName] = "";
      }
      return acc;
    }, {});

    setFormData((prev) => ({ ...prev, ...initValues }));

    setErrors((prev) => {
      const next = { ...prev };
      formatted.forEach((f) => delete next[f.fieldName]);
      return next;
    });
  }, [rawFields]);

  const clearError = (field) => setErrors((prev) => ({ ...prev, [field]: "" }));

  const handleChange = (field, value) => {
    clearError(field);

    if (field === "country_code") {
      if (!value) {
        setFormData((prev) => ({
          ...prev,
          country_name: "",
          country_code: "",
          account_currency: "",
          bank_country_code: "",
          payment_method: "",
          transfer_method: "",
        }));

        setMethods({});
        setRawFields([]);
        setAccountFields([]);
        setAddressFields([]);
        setErrors({});
        setStep(STEP_MAIN);
        return;
      }

      const found = countryList?.find(
        (c) => c.countryName.toLowerCase() === value.toLowerCase(),
      );

      if (found) {
        setFormData((prev) => {
          const cleared = {
            ...prev,
            country_name: found.countryName,
            country_code: found.countryCode2,
            account_currency: found.currency,
            bank_country_code: found.countryCode2,
            payment_method: "",
            transfer_method: "",
          };

          [...accountFields, ...addressFields].forEach((f) => {
            cleared[f.fieldName] = "";
          });

          return cleared;
        });

        setErrors({});
        setMethods({});
        setRawFields([]);
        setAccountFields([]);
        setAddressFields([]);
        setStep(STEP_MAIN);
      }

      return;
    }

    if (field === "swift_code") {
      setFormData((prev) => ({
        ...prev,
        [field]: (value ?? "").toUpperCase(),
      }));
      return;
    }

    if (field === "account_routing_value1") {
      setFormData((prev) => ({
        ...prev,
        account_routing_value1: (value ?? "").toUpperCase(),
        account_routing_type1: getFirstValidRoutingType(
          rawFields,
          "account_routing_type1",
        ),
      }));
      return;
    }

    if (field === "account_routing_value2") {
      setFormData((prev) => ({
        ...prev,
        account_routing_value2: (value ?? "").toUpperCase(),
        account_routing_type2: getFirstValidRoutingType(
          rawFields,
          "account_routing_type2",
        ),
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: capitaliseFirst(value) }));
  };

  const handleInputChange = (e) => handleChange(e.target.name, e.target.value);

  const handleContinueToAddress = () => {
    const trimmed = trimFormData(formData);
    const { errors: stepErrors, isValid } = validateMainStep(
      trimmed,
      accountFields,
    );
    if (!isValid) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    setStep(STEP_ADDRESS);
  };

  const handleBackToMain = () => {
    setErrors({});
    setStep(STEP_MAIN);
  };

  const handleSubmit = async () => {
    if (addressFields.length > 0 && step === STEP_MAIN) {
      handleContinueToAddress();
      return;
    }

    const trimmed = trimFormData(formData);

    if (step === STEP_ADDRESS) {
      const { errors: stepErrors, isValid } = validateAddressStep(
        trimmed,
        addressFields,
      );
      if (!isValid) {
        setErrors(stepErrors);
        return;
      }
    } else {
      const { errors: stepErrors, isValid } = validateMainStep(
        trimmed,
        accountFields,
      );
      if (!isValid) {
        setErrors(stepErrors);
        return;
      }
    }

    setLoading(true);
    setErrors({});
    try {
      const dynamicValues = [...accountFields, ...addressFields].reduce(
        (acc, f) => {
          acc[f.fieldName] = formData[f.fieldName] ?? "";
          return acc;
        },
        {},
      );

      const payload = {
        userId,
        ...dynamicValues,
        entity_type:
          formData.entity_type === "INDIVIDUAL" ? "PERSONAL" : "COMPANY",
        first_name: formData.first_name,
        last_name: formData.last_name,
        country_code: formData.country_code,
        country_name: formData.country_name,
        account_currency: formData.account_currency,
        bank_country_code: formData.bank_country_code,
        transfer_method: formData.payment_method,
        account_name: formData.first_name,
        account_routing_type1: formData.account_routing_type1 ?? "",
        account_routing_value1: formData.account_routing_value1 ?? "",
        account_routing_type2: formData.account_routing_type2 ?? "",
        account_routing_value2: formData.account_routing_value2 ?? "",
      };

      const response = await AddNewBeneficiary(payload);
      const resData = response?.data;

      if (
        (response?.status === 200 || resData?.status === 200) &&
        resData?.data
      ) {
        const newBeneficiaryId = resData?.data?.beneficiaryId;

        if (newBeneficiaryId) {
          setPollingMessage(true);
          const resolved = await pollForStatus(userId, newBeneficiaryId);

          setPollingMessage(false);

          if (!resolved) {
            setErrors({
              submit: [
                "Beneficiary verification is taking longer than expected. Please check the beneficiary list and try again.",
              ],
            });

            setLoading(false);
            setTimeout(() => {
              onCancel?.();
              onSuccess?.();
            }, 4000);

            return;
          }

          if (resolved.status === "REJECTED") {
            setErrors({
              submit: [
                resolved.errorMsg ||
                  "Beneficiary was rejected. Please check the details and try again.",
              ],
            });
            setLoading(false);
            return;
          }
        }

        setSuccessMessage("Beneficiary created successfully!");
        setFormData(INITIAL_FORM);
        setMethods({});
        setRawFields([]);
        setAccountFields([]);
        setAddressFields([]);
        setErrors({});
        setStep(STEP_MAIN);

        setTimeout(() => {
          onSuccess?.(resData.data);
          setSuccessMessage("");
        }, 2500);
      } else if (response?.status === 422 || resData?.status === 422) {
        setErrors({
          submit: [resData?.message || "Beneficiary already exists"],
        });
      } else {
        const errorData =
          resData?.error || resData?.message || "Failed to create beneficiary.";
        setErrors({
          submit: Array.isArray(errorData) ? errorData : [errorData],
        });
      }
    } catch (error) {
      setErrors({
        submit: [
          error?.response?.data?.message || "Error creating beneficiary.",
        ],
      });
    } finally {
      setLoading(false);
    }
  };
  const renderDynamicField = (field) => (
    <Col md={12} className={styles.inputDiv} key={field.fieldName}>
      <Input
        label={field.displayName}
        name={field.fieldName}
        placeholder={`Enter ${field.displayName}`}
        value={formData[field.fieldName] || ""}
        onChange={handleInputChange}
        error={!!errors[field.fieldName]}
        helperText={errors[field.fieldName]}
        required={field.required}
      />
    </Col>
  );

  const renderMethodCard = (methodKey, meta) => {
    const isSelected = formData.payment_method === methodKey;
    return (
      <Col md={12} className={styles.inputDiv} key={methodKey}>
        <div
          className={`${styles.methodCard} ${isSelected ? styles.methodCardSelected : ""}`}
          onClick={() => {
            clearError("payment_method");
            setFormData((prev) => {
              const updated = {
                ...prev,
                payment_method: methodKey,
                transfer_method: methodKey,
              };
              [...accountFields, ...addressFields].forEach((field) => {
                updated[field.fieldName] = "";
              });

              return updated;
            });
            setErrors((prev) => {
              const updatedErrors = { ...prev };
              [...accountFields, ...addressFields].forEach((field) => {
                delete updatedErrors[field.fieldName];
              });
              return updatedErrors;
            });
          }}
        >
          <div className={styles.methodCardHeader}>
            <div>
              <div className={styles.methodCardTitle}>{meta.title}</div>
              <div className={styles.methodCardDuration}>{meta.duration}</div>
              {meta.charges && (
                <div className={styles.methodCardCharges}>
                  Charges: {meta.charges}
                </div>
              )}
            </div>
            <input
              type="radio"
              readOnly
              checked={isSelected}
              style={{ marginTop: "4px", accentColor: "#4f46e5" }}
            />
          </div>
          {meta.recommended && (
            <span className={styles.methodCardBadge}>Recommended</span>
          )}
        </div>
      </Col>
    );
  };

  const renderStatusBanners = () => (
    <>
      {successMessage && (
        <div className={styles.successBanner}>{successMessage}</div>
      )}
      {errors.submit && (
        <div className={styles.errorBanner}>
          {errors.submit.map((err, i) => (
            <div key={i}>{err}</div>
          ))}
        </div>
      )}
    </>
  );

  const countryOptions = countryList.map((c) => ({
    label: c.countryName,
    value: c.countryName,
  }));

  const hasAddressFields = addressFields.length > 0;

  if (step === STEP_MAIN) {
    return (
   <div className={styles.lienBeneficiaryForm} style={{ position: "relative" }}>
          {(loading || pollingMessage) && (
            <div className={styles.fullScreenOverlay}>
              <Spinner animation="border" />
            </div>
          )}
        <div className={styles.formScrollArea}>
          <Row style={{ margin: "8px 0", width: "100%" }}>
            <Col md={12} className={styles.inputDiv}>
              <DropDown
                label="Payee Type"
                name="entity_type"
                required
                placeholder="Select Payee Type"
                data={ENTITY_OPTIONS}
                value={formData.entity_type}
                onChange={(val) => {
                  clearError("entity_type");
                  setFormData((prev) => ({ ...prev, entity_type: val }));
                }}
                error={!!errors.entity_type}
                helperText={errors.entity_type}
                isSearchable={false}
              />
            </Col>

            <Col md={12} className={styles.inputDiv}>
              <Input
                label="First Name"
                name="first_name"
                placeholder="Enter First Name"
                value={formData.first_name}
                onChange={handleInputChange}
                error={!!errors.first_name}
                helperText={errors.first_name}
                required
              />
            </Col>

            <Col md={12} className={styles.inputDiv}>
              <Input
                label="Last Name"
                name="last_name"
                placeholder="Enter Last Name"
                value={formData.last_name}
                onChange={handleInputChange}
                error={!!errors.last_name}
                helperText={errors.last_name}
                required
              />
            </Col>

            <Col md={12} className={styles.inputDiv}>
              <DropDown
                label="Payee Country"
                name="country_code"
                required
                placeholder="Select Country"
                value={formData.country_name}
                data={countryOptions}
                onChange={(val) => handleChange("country_code", val)}
                error={!!errors.country_code}
                helperText={errors.country_code}
                isSearchable
                loading={countryLoading}
              />
            </Col>

            {formData.country_code && (
              <>
                <Col md={12} className={styles.inputDiv}>
                  <label className={styles.sectionLabel}>
                    Choose Transfer Method{" "}
                    <span className={styles.required}>*</span>
                  </label>
                </Col>

                {methodsLoading ? (
                  <Col
                    md={12}
                    className={`${styles.inputDiv} ${styles.spinnerRow}`}
                  >
                    <Spinner animation="border" size="sm" />
                  </Col>
                ) : Object.keys(methods).length > 0 ? (
                  Object.entries(methods).map(([key, meta]) =>
                    renderMethodCard(key, meta),
                  )
                ) : (
                  <Col md={12} className={styles.inputDiv}>
                    <p className={styles.noMethodsText}>
                      No transfer methods available for selected country.
                    </p>
                  </Col>
                )}

                {errors.payment_method && (
                  <Col md={12}>
                    <p
                      className={styles.errorMessage}
                      style={{ marginTop: "-4px", marginBottom: "8px" }}
                    >
                      {errors.payment_method}
                    </p>
                  </Col>
                )}
              </>
            )}

            {accountFields.map((field) => renderDynamicField(field))}
          </Row>

          {renderStatusBanners()}
        </div>

        <div className={styles.formFooter}>
          <div className={styles.footerRow}>
            {onCancel && (
              <div className={styles.footerCol}>
                <Button onClick={onCancel} title="Cancel" disabled={loading} />
              </div>
            )}
            <div className={styles.footerCol}>
              <Button
                onClick={
                  hasAddressFields ? handleContinueToAddress : handleSubmit
                }
                title="Continue"
                loading={!hasAddressFields && loading}
                disabled={loading}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.lienBeneficiaryForm} style={{ position: "relative" }}>
      {(loading || pollingMessage) && (
        <div className={styles.fullScreenOverlay}>
          <Spinner animation="border" />
        </div>
      )}
      <div className={styles.formScrollArea}>
        <div className={styles.addressScreenHeader}>
          <button
            type="button"
            className={styles.backButton}
            onClick={handleBackToMain}
            aria-label="Go back"
          >
            <img src={singleBackArrow} alt="Back" className={styles.backIcon} />
          </button>
          <h6 className={styles.addressScreenTitle}>Address Details</h6>
        </div>

        <Row style={{ margin: "0", width: "100%" }}>
          {addressFields.map((field) => renderDynamicField(field))}
        </Row>

        {renderStatusBanners()}
      </div>

      <div className={styles.formFooter}>
        <div className={styles.footerRow}>
          <div className={styles.footerCol}>
            <Button
              onClick={handleBackToMain}
              title="Cancel"
              disabled={loading}
            />
          </div>
          <div className={styles.footerCol}>
            <Button
              onClick={handleSubmit}
              title="Add Beneficiary"
              disabled={loading}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default LienAddBeneficiary;
